"use client";

import { useEffect, useState } from "react";
import { DrawerFieldShell } from "@aera/data-table";
import { AeraButton } from "@aera/ui";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  getReceivableOrderId,
  isReceivableSlipItem,
  resolveOwnerUserId,
} from "@/lib/pendingOrders";
import {
  canEditPendingSlipSender,
  pickSlipSenderName,
} from "@/lib/receivableCorrections";
import { postEditPendingSlipSender } from "@/lib/moneyOperations";
import { showToast } from "@/app/components/ui/Toast";

const INPUT_CLASS =
  "w-full border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-aera-charcoal shadow-none placeholder:text-aera-lightslate placeholder:font-normal focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-aera-lightslate";

function fieldValueForInput(value: string, fallback = ""): string {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "—") return fallback;
  return trimmed;
}

export function ReceivableSlipSenderEditor({
  item,
  label,
  value,
  ownerUserId,
  workspaceId,
  disabled = false,
  onApplied,
}: {
  item: ReceivableListItem;
  label: string;
  value: string;
  ownerUserId: string | null;
  workspaceId: string | null;
  disabled?: boolean;
  onApplied?: () => void | Promise<void>;
}) {
  const orderId = getReceivableOrderId(item);
  const originalName = pickSlipSenderName(item);
  const editable =
    canEditPendingSlipSender(item) && isReceivableSlipItem(item);

  const [editName, setEditName] = useState(() =>
    fieldValueForInput(value, originalName),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEditName(fieldValueForInput(value, originalName));
    setError(null);
  }, [item.mergeKey, originalName, value]);

  const resolvedOwner =
    ownerUserId?.trim() || resolveOwnerUserId()?.trim() || "";
  const canAct = Boolean(editable && resolvedOwner && orderId && !disabled);

  const trimmed = editName.trim();
  const unchanged = trimmed === originalName.trim();
  const invalid = !trimmed;

  if (!editable) {
    return null;
  }

  const handleSave = async () => {
    if (!canAct || !orderId || !resolvedOwner || unchanged || invalid) return;

    setBusy(true);
    setError(null);
    try {
      const result = await postEditPendingSlipSender({
        ownerUserId: resolvedOwner,
        workspaceId: workspaceId ?? undefined,
        orderId,
        slipSender: trimmed,
      });
      showToast(result.summary_lines?.[0] || "บันทึกผู้โอนแล้ว");
      await onApplied?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "แก้ชื่อผู้โอนไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 px-4 py-3">
      <label
        htmlFor="receivable-field-slip_sender"
        className="block text-xs font-semibold leading-5 text-aera-lightslate"
      >
        {label}
      </label>
      <DrawerFieldShell editable={!disabled && canAct}>
        <input
          id="receivable-field-slip_sender"
          type="text"
          value={editName}
          disabled={!canAct || busy}
          placeholder="ยังไม่ระบุ"
          onChange={(event) => setEditName(event.target.value)}
          className={INPUT_CLASS}
        />
      </DrawerFieldShell>

      {!unchanged && invalid ? (
        <p className="text-xs font-medium text-aera-red">กรุณาระบุชื่อผู้โอน</p>
      ) : null}

      {!unchanged && canAct && !invalid ? (
        <AeraButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => void handleSave()}
        >
          {busy ? "กำลังบันทึก…" : "บันทึกผู้โอน"}
        </AeraButton>
      ) : null}

      {error ? (
        <p className="text-xs font-medium text-aera-red">{error}</p>
      ) : null}
    </div>
  );
}
